import { useState } from 'react';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@xiltrion/ui/popover';
import { Icono } from '@xiltrion/ui/icono';
import { Item, ItemContent, ItemDescription, ItemTitle } from '@xiltrion/ui/item';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/api/client';
import type { Notification } from '@/api/types';
import { fmtDate } from '@/components/ui';

/** Notification center. */
export function NotificationsBell({ count }: { count: number }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['notifications', 'list'], queryFn: () => apiGet<Notification[]>('/notifications'), enabled: false });
  const readAll = useMutation({ mutationFn: () => apiPost('/notifications/read-all'), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  const readOne = useMutation({ mutationFn: (id: string) => apiPost(`/notifications/${id}/read`), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) void list.refetch(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Notificaciones${count ? `, ${count} sin leer` : ''}`} className="relative">
          <Icono nombre='mdi:bell-outline' className="size-5" aria-hidden />
          {count > 0 && <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] leading-none">{count > 99 ? '99+' : count}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(380px,95vw)] p-3">
        <div className="flex items-center justify-between mb-2"><strong>Notificaciones</strong><Button variant="ghost" size="sm" onClick={() => readAll.mutate()}>Marcar todas leídas</Button></div>
        <div className="max-h-96 overflow-y-auto flex flex-col gap-1">
          {list.isFetching && !list.data ? <div className="text-muted-foreground p-2">Cargando…</div> : !list.data?.length ? <div className="text-muted-foreground p-2">No tienes notificaciones. Cuando ocurra algo relevante en tus casos aparecerá aquí.</div> : list.data.map((n) => (
            <Item key={n.id} asChild size="sm" className={`p-2 text-left cursor-pointer ${n.readAt ? 'bg-transparent' : 'bg-primary/10'} hover:bg-accent`}>
              <button type="button" onClick={() => { readOne.mutate(n.id); setOpen(false); if (n.type === 'EMAIL_FAILED' && n.caseId) nav(`/pqrs/${n.caseId}?tab=comunicaciones`); else if (n.type.startsWith('SUPPORT_') && n.caseId) nav(`/pqrs/${n.caseId}?tab=apoyos`); else if (n.taskId) nav(`/tareas/${n.taskId}`); else if (n.caseId) nav(`/pqrs/${n.caseId}`); else if (n.type === 'SETUP_ATTENTION') nav('/puesta-en-marcha'); }}>
                <ItemContent className="gap-0 min-w-0">
                  <ItemTitle className="wrap-anywhere">{n.title}</ItemTitle>
                  {n.body && <ItemDescription className="line-clamp-3 wrap-anywhere text-[13px]" title={n.body}>{n.body}</ItemDescription>}
                  <div className="text-muted-foreground/70 text-xs">{fmtDate(n.createdAt)}</div>
                </ItemContent>
              </button>
            </Item>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
